import { arrayRemove, arrayUnion } from "firebase/firestore";

export const normalizeContentBoxIds = (contentBoxIds = []) => {
  const seen = new Set();
  const normalized = [];

  (Array.isArray(contentBoxIds) ? contentBoxIds : []).forEach((id) => {
    const value = String(id || "").trim();
    if (!value || seen.has(value)) {
      return;
    }
    seen.add(value);
    normalized.push(value);
  });

  return normalized;
};

/**
 * @deprecated Prefer appendContentBoxIdsToOrderBatch inside a writeBatch.
 */
export const buildContentBoxesOrderArrayUnionUpdate = (contentBoxIds = []) => {
  const normalizedIds = normalizeContentBoxIds(contentBoxIds);
  if (!normalizedIds.length) {
    return {};
  }

  return {
    contentBoxesOrder: arrayUnion(...normalizedIds),
  };
};

/**
 * Appends content box IDs to the class order using Firestore arrayUnion.
 * Safe for concurrent writers — never replaces the full server array.
 */
export const appendContentBoxIdsToOrderBatch = (batch, classRef, contentBoxIds = []) => {
  const normalizedIds = normalizeContentBoxIds(contentBoxIds);
  if (!normalizedIds.length) {
    return false;
  }

  batch.update(classRef, {
    contentBoxesOrder: arrayUnion(...normalizedIds),
  });
  return true;
};

/**
 * Removes content box IDs from the class order using Firestore arrayRemove.
 */
export const removeContentBoxIdsFromOrderBatch = (batch, classRef, contentBoxIds = []) => {
  const normalizedIds = normalizeContentBoxIds(contentBoxIds);
  if (!normalizedIds.length) {
    return false;
  }

  batch.update(classRef, {
    contentBoxesOrder: arrayRemove(...normalizedIds),
  });
  return true;
};

/**
 * Privileged helper: replaces the entire contentBoxesOrder array in a batch.
 *
 * serverOrder MUST come from getDoc(classRef) in the same function, immediately
 * before computing nextOrder. Do NOT pass Redux classData.contentBoxesOrder.
 */
export const replaceContentBoxesOrderBatchAfterFreshRead = ({
  batch,
  classRef,
  nextOrder,
  serverOrder,
}) => {
  if (!batch || !classRef) {
    throw new Error("replaceContentBoxesOrderBatchAfterFreshRead requires batch and classRef.");
  }

  if (!Array.isArray(serverOrder)) {
    throw new Error(
      "replaceContentBoxesOrderBatchAfterFreshRead requires serverOrder from a fresh getDoc(classRef)."
    );
  }

  if (!Array.isArray(nextOrder)) {
    throw new Error("replaceContentBoxesOrderBatchAfterFreshRead requires nextOrder array.");
  }

  batch.update(classRef, {
    contentBoxesOrder: [...nextOrder],
  });

  return true;
};

export const mergeLocalContentBoxesOrder = (localOrder, newIds = []) => {
  const base = Array.isArray(localOrder) ? [...localOrder] : [];
  const existing = new Set(base.map(String));

  normalizeContentBoxIds(newIds).forEach((id) => {
    if (existing.has(id)) {
      return;
    }
    base.push(id);
    existing.add(id);
  });

  return base;
};

/**
 * Inserts missing IDs into contentBoxesOrder while preserving the relative order of
 * `desiredOrderedIds`. Existing anchor IDs (e.g. extended multi-day boxes) stay put;
 * newly copied IDs are inserted before/after those anchors instead of always appending.
 */
export const mergeOrderedIdsIntoContentBoxesOrder = (currentOrder = [], desiredOrderedIds = []) => {
  const order = normalizeContentBoxIds(currentOrder);
  const desired = normalizeContentBoxIds(desiredOrderedIds);
  if (!desired.length) {
    return order;
  }

  desired.forEach((id, index) => {
    if (order.includes(id)) {
      return;
    }

    let insertAt = order.length;
    for (let previousIndex = index - 1; previousIndex >= 0; previousIndex -= 1) {
      const anchorIndex = order.indexOf(desired[previousIndex]);
      if (anchorIndex !== -1) {
        insertAt = anchorIndex + 1;
        break;
      }
    }

    if (insertAt === order.length) {
      for (let nextIndex = index + 1; nextIndex < desired.length; nextIndex += 1) {
        const anchorIndex = order.indexOf(desired[nextIndex]);
        if (anchorIndex !== -1) {
          insertAt = anchorIndex;
          break;
        }
      }
    }

    order.splice(insertAt, 0, id);
  });

  return order;
};

/**
 * Simulates Firestore arrayUnion append behavior against a server-side order.
 * Used by tests to prove stale local order must not replace the server array.
 */
export const appendContentBoxIdsToServerOrder = (serverOrder, newIds = []) => {
  return mergeLocalContentBoxesOrder(serverOrder, newIds);
};
